import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/Bits";
import { Save, BookOpen, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

// Free-form optional prompts the user can click to inject as section headers.
// Click any of these and the text appears at the cursor / end of the entry as a
// bold-style line; you can answer freely below it. No required structure.
const OPTIONAL_PROMPTS = [
  "What worked today?",
  "What didn't work?",
  "Biggest mistake?",
  "Best trade — why?",
  "Worst trade — why?",
  "Mood / energy?",
  "Rule I broke?",
  "Lesson to take forward?",
  "Plan for tomorrow?",
  "Setup hit-rate today?",
  "Stuck-point of the day?",
  "Wins outside of P&L?",
];

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Journal() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get("date") || todayStr();
  const [date, setDate] = useState(initialDate);
  const [text, setText] = useState("");

  const { data: entry } = useQuery({
    queryKey: ["journal", date],
    queryFn: async () => (await api.get(`/journal/${date}`)).data,
  });
  const { data: entries = [] } = useQuery({
    queryKey: ["journal-list"],
    queryFn: async () => (await api.get("/journal")).data,
  });

  /* eslint-disable */
  useEffect(() => {
    if (entry) {
      // Prefer existing free-form body. Fall back to the recap summary so the user
      // doesn't lose what Coach piped in.
      setText(entry.body || entry.summary || "");
    }
  }, [entry]);
  /* eslint-enable */

  const saveMut = useMutation({
    mutationFn: async () => (await api.put(`/journal/${date}`, { date, body: text, summary: text })).data,
    onSuccess: () => {
      toast.success("JOURNAL SAVED");
      qc.invalidateQueries({ queryKey: ["journal-list"] });
      qc.invalidateQueries({ queryKey: ["journal", date] });
      qc.invalidateQueries({ queryKey: ["calendar-day"] });
    },
    onError: () => toast.error("Failed to save"),
  });

  const insertPrompt = (prompt) => {
    const sep = text && !text.endsWith("\n\n") ? "\n\n" : "";
    setText((t) => `${t}${sep}${prompt}\n`);
  };

  return (
    <div>
      <PageHeader eyebrow="DAILY DEBRIEF" title="Trading Journal"
        action={
          <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="save-journal-btn"
            className="flex items-center gap-2 px-3 py-2 text-xs bg-[color:var(--accent)] text-black hover:bg-[color:var(--accent)] transition-colors disabled:opacity-50">
            <Save className="h-3.5 w-3.5" strokeWidth={1.5} /> {saveMut.isPending ? "SAVING…" : "SAVE ENTRY"}
          </button>
        }
      />

      <div className="p-8 grid lg:grid-cols-[1fr_300px] gap-6">
        {/* Main: date + free-form journal */}
        <div className="space-y-5">
          <div className="flex items-center gap-4 border border-[color:var(--border)] p-3">
            <span className="label-eyebrow">DATE</span>
            <input
              type="date" value={date} onChange={(e) => setDate(e.target.value)}
              data-testid="journal-date-input"
              className="bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1 text-xs font-mono-num focus:outline-none focus:border-[color:var(--accent)]"
            />
            <span className="text-xs text-[color:var(--fg-3)] font-mono-num ml-auto">
              {entry?.pnl !== undefined && (
                <>P&L: <span className={(entry.pnl ?? 0) >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}>${(entry.pnl ?? 0).toFixed(2)}</span> · {entry.trades ?? 0} TRADES</>
              )}
            </span>
          </div>

          {entry?.summary_source === "recap" && entry.summary && !text.includes(entry.summary) && (
            <div className="border border-[rgba(34,197,94,0.20)] bg-[rgba(168,85,247,0.05)] p-3 text-[11px] font-mono-num text-[color:var(--accent)] flex items-center gap-2" data-testid="recap-prepopulated">
              <Sparkles className="h-3 w-3" strokeWidth={1.5} />
              YOUR RECAP SUMMARY FOR {date} WAS PRE-FILLED ABOVE. EDIT FREELY.
            </div>
          )}

          <Card eyebrow="ENTRY" title="Write whatever comes to mind">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={22}
              data-testid="journal-body"
              placeholder="Today I…  (use the prompts on the right if you want some structure)"
              className="w-full bg-[color:var(--bg)] border border-[color:var(--border)] px-4 py-4 text-sm focus:outline-none focus:border-[color:var(--accent)] resize-none leading-relaxed font-mono-num"
            />
            <div className="mt-2 text-[11px] text-[color:var(--fg-3)] font-mono-num">
              {text.length} characters · auto-saves nothing, hit SAVE ENTRY when done
            </div>
          </Card>
        </div>

        {/* Sidebar: prompts + history */}
        <div className="space-y-6">
          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-2"><Plus className="h-3 w-3" strokeWidth={1.5}/> OPTIONAL PROMPTS</div>
            <div className="text-[10px] text-[color:var(--fg-3)] mb-2 leading-relaxed font-mono-num">
              Click any prompt to drop it into your entry as a heading. Use them only when you want structure.
            </div>
            <div className="flex flex-wrap gap-1.5">
              {OPTIONAL_PROMPTS.map((p, i) => (
                <button key={i}
                  onClick={() => insertPrompt(p)}
                  data-testid={`prompt-${i}`}
                  className="text-left text-[11px] px-2 py-1.5 border border-[color:var(--border)] hover:border-[color:var(--accent)]/50 hover:bg-[rgba(168,85,247,0.05)] text-[color:var(--fg-2)] hover:text-[color:var(--accent)] transition-colors font-mono-num"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-2"><BookOpen className="h-3 w-3" strokeWidth={1.5}/> HISTORY</div>
            <div className="border border-[color:var(--border)] max-h-[600px] overflow-y-auto">
              {entries.length === 0 ? (
                <div className="p-4 text-xs text-[color:var(--fg-3)] font-mono-num">No entries yet.</div>
              ) : entries.map((e) => (
                <button key={e.date} onClick={() => setDate(e.date)}
                  data-testid={`history-${e.date}`}
                  className={`w-full text-left p-3 border-b border-[color:var(--border)] hover:bg-[color:var(--surface)]/60 transition-colors ${date === e.date ? "bg-[color:var(--surface)]/80 border-l-2 border-l-[color:var(--accent)]" : ""}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono-num text-[color:var(--fg)]">{e.date}</span>
                    <span className={`text-xs font-mono-num ${(e.pnl ?? 0) >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]"}`}>
                      ${(e.pnl ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="text-xs text-[color:var(--fg-3)] mt-1 line-clamp-2">{e.body || e.summary || "—"}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
